import { Extension } from '@codemirror/state';
import { PlusIcon } from '@langchain/untitled-ui-icons';
import { Button, FormLabel, Input, Radio, Stack, Textarea } from '@mui/joy';

import {
  Dispatch,
  RefObject,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { StructuredPromptSchema } from '@/Pages/Playground/components/manifest/serialized/prompts/ManifestStructuredPrompt';
import { Tooltip } from '@/design-system';
import InfoCircleIcon from '@/icons/InfoCircleIcon.svg?react';
import { capitalize, pluralize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { CodeDataEditor } from '../Code/Code';
import { CodeLanguageType } from '../Code/types';
import { ParametersEditorView } from '../ExampleCrudPane/EditInputFunctions';
import { JsonSchema7 } from './JsonSchema';
import { isJsonSchema7 } from './JsonSchema.guard';
import { requiredEntriesToRequired } from './schemaUtils';
import {
  SchemaEditorVariant,
  VARIANT_LABEL_MAP,
  VARIANT_PROPERTY_LABEL_MAP,
} from './types';

export function SchemaEditor({
  value,
  onChange,
  isEditor,
  language,
  variant = SchemaEditorVariant.FUNCTIONS,
  readOnly = false,
  codeEditorExtensions,
  schemaScrollRef,
  schemaError,
  setSchemaError,
}: {
  value: StructuredPromptSchema;
  onChange?: Dispatch<SetStateAction<StructuredPromptSchema>>;
  isEditor: boolean;
  language: CodeLanguageType;
  variant?: SchemaEditorVariant;
  readOnly?: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  schemaScrollRef?: RefObject<HTMLDivElement>;
  schemaError?: string | undefined;
  setSchemaError?: Dispatch<SetStateAction<string | undefined>>;
}) {
  const isEditorAvailable = isJsonSchema7(value) && value.type === 'object';

  const schemaTitle = isEditorAvailable ? value.title : undefined;
  useEffect(() => {
    if (!isEditorAvailable || !setSchemaError) return;
    if (schemaTitle?.includes(' ')) {
      const error = `${VARIANT_LABEL_MAP[variant]} name cannot contain spaces`;
      setSchemaError(error);
    } else {
      setSchemaError(undefined);
    }
  }, [schemaTitle, isEditorAvailable, setSchemaError, variant]);

  return (
    <div className="group flex flex-col rounded-md">
      {isEditorAvailable && isEditor ? (
        (!readOnly || !!schemaTitle || !!value.description) && (
          <Stack gap={2}>
            <Stack gap={1}>
              <Stack direction={'row'} gap={1}>
                <FormLabel>Name</FormLabel>
              </Stack>
              <div className="flex flex-col gap-2">
                <Input
                  data-testid="schema-title-input"
                  value={schemaTitle ?? ''}
                  size="sm"
                  placeholder="Tool name"
                  readOnly={readOnly}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    onChange?.((prev) => ({ ...prev, title: newValue }));
                  }}
                  error={!!schemaError}
                  autoFocus={true}
                />
                <div className="text-sm text-red-500">{schemaError}</div>
              </div>
            </Stack>
            <Stack gap={1}>
              <FormLabel>Description</FormLabel>
              <Textarea
                data-testid="schema-description-input"
                value={value.description ?? ''}
                size="sm"
                placeholder="Description"
                readOnly={readOnly}
                minRows={1}
                maxRows={10}
                onChange={(event) => {
                  onChange?.((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }));
                }}
              />
            </Stack>

            <FunctionParametersEditor
              properties={value.properties}
              required={value.required}
              strict={value.strict}
              additionalProperties={value.additionalProperties}
              onChange={onChange}
              variant={variant}
              readOnly={readOnly}
              codeEditorExtensions={codeEditorExtensions}
              nestingLevel={0}
              schemaScrollRef={schemaScrollRef}
            />
          </Stack>
        )
      ) : (
        <CodeDataEditor
          value={value}
          language={language}
          onChange={(parameters) => {
            onChange && onChange(parameters);
          }}
          readOnly={readOnly}
          extensions={codeEditorExtensions?.[language] ?? []}
        />
      )}
    </div>
  );
}

const ParameterEditor = memo(
  ({
    propertyKey,
    idx,
    property,
    variant,
    readOnly,
    codeEditorExtensions,
    nestingLevel,
    required,
    isDuplicate,
    handleInputChange,
    handleRequiredChange,
    handleDelete,
    handleChildChange,
  }: {
    property: JsonSchema7;
    variant: SchemaEditorVariant;
    readOnly: boolean;
    codeEditorExtensions?: Record<string, Extension[]>;
    nestingLevel: number;
    required: boolean;
    propertyKey: string;
    idx: number;
    isDuplicate: boolean;
    handleInputChange: (newKey: string, idx: number) => void;
    handleRequiredChange: (required: boolean, idx: number) => void;
    handleDelete: (idx: number) => void;
    handleChildChange: (newValue: JsonSchema7, idx: number) => void;
  }) => {
    const onInputChange = useCallback(
      (e) => {
        const newKey = e.target.value;
        handleInputChange(newKey, idx);
      },
      [handleInputChange, idx]
    );

    const onRequiredChange = useCallback(
      (e) => {
        handleRequiredChange(e.target.checked, idx);
      },
      [handleRequiredChange, idx]
    );

    const onDelete = useCallback(() => {
      handleDelete(idx);
    }, [handleDelete, idx]);

    const onItemChange = useCallback(
      (newValue) => {
        handleChildChange(newValue, idx);
      },
      [handleChildChange, idx]
    );

    return (
      <ParametersEditorView
        key={idx}
        value={property}
        variant={variant}
        readOnly={readOnly}
        codeEditorExtensions={codeEditorExtensions}
        propertyKey={propertyKey}
        onInputChange={onInputChange}
        onRequiredChange={onRequiredChange}
        onDelete={onDelete}
        onItemChange={onItemChange}
        nestingLevel={nestingLevel}
        required={required}
        isDuplicate={isDuplicate}
      />
    );
  }
);

const FunctionParametersEditor = memo(function FunctionParametersEditor({
  properties,
  required,
  strict,
  additionalProperties,
  onChange,
  variant,
  readOnly,
  codeEditorExtensions,
  nestingLevel,
  schemaScrollRef,
}: {
  properties:
    | {
        [property: string]: JsonSchema7;
      }
    | undefined;
  required: string[] | undefined;
  strict: boolean | undefined;
  additionalProperties: boolean | JsonSchema7 | undefined;
  onChange?: Dispatch<SetStateAction<any>>;
  variant: SchemaEditorVariant;
  readOnly: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  nestingLevel: number;
  schemaScrollRef?: RefObject<HTMLDivElement>;
}) {
  const [propertyState, setPropertyState] = useState(() => {
    const propertyEntries = Object.entries(properties ?? {});
    const requiredEntries = propertyEntries.map(([key]) => ({
      key,
      required: required?.includes(key) ?? false,
    }));
    return { propertyEntries, requiredEntries };
  });

  const { propertyEntries, requiredEntries } = propertyState;

  const propKeys = propertyEntries.map(([key]) => key);
  const [orderKeys, setOrderKeys] = useState(() => propKeys);

  // update property state when properties change
  useEffect(() => {
    const _propEntries = Object.entries(properties ?? {});
    setPropertyState({
      propertyEntries: _propEntries,
      requiredEntries: _propEntries.map(([key]) => ({
        key,
        required: required?.includes(key) ?? false,
      })),
    });
    const _propKeys = _propEntries.map(([key]) => key);
    setOrderKeys(_propKeys);
  }, [properties, required]);

  const handleChildChange = useCallback(
    (newValue: JsonSchema7, idx: number) => {
      setPropertyState((prevState) => {
        const updatedEntries = [...prevState.propertyEntries];
        updatedEntries[idx][1] = newValue;
        onChange?.((prev) => ({
          ...prev,
          properties: Object.fromEntries(updatedEntries),
        }));
        return {
          ...prevState,
          propertyEntries: updatedEntries,
        };
      });
    },
    [onChange]
  );

  const handleRequiredChange = useCallback(
    (required: boolean, idx: number) => {
      setPropertyState((prevState) => {
        const updatedRequired = [...prevState.requiredEntries];
        updatedRequired[idx].required = required;
        const newRequired = requiredEntriesToRequired(updatedRequired);
        onChange?.((prev) => ({ ...prev, required: newRequired }));
        return {
          ...prevState,
          requiredEntries: updatedRequired,
        };
      });
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (newKey: string, idx: number) => {
      setPropertyState((prevState) => {
        const updatedRequired = [...prevState.requiredEntries];
        const updatedEntries = [...prevState.propertyEntries];
        updatedRequired[idx].key = newKey;
        updatedEntries[idx][0] = newKey;
        const hasDuplicateKeys =
          new Set(updatedEntries.map(([k]) => k)).size !==
          updatedEntries.length;
        if (!hasDuplicateKeys) {
          const newRequired = requiredEntriesToRequired(updatedRequired);
          onChange?.((prev) => ({
            ...prev,
            required: newRequired,
            properties: Object.fromEntries(updatedEntries),
          }));
        }
        return {
          ...prevState,
          requiredEntries: updatedRequired,
          propertyEntries: updatedEntries,
        };
      });
      setOrderKeys((prev) => {
        const updatedOrderKeys = [...prev];
        updatedOrderKeys[idx] = newKey;
        return updatedOrderKeys;
      });
    },
    [onChange]
  );

  const handleDelete = useCallback(
    (idx: number) => {
      const filterPredicate = (_, prevIdx) => idx !== prevIdx;

      setPropertyState((prevState) => {
        const updatedEntries =
          prevState.propertyEntries.filter(filterPredicate);
        const updatedRequired =
          prevState.requiredEntries.filter(filterPredicate);

        const newProperties = Object.fromEntries(updatedEntries);
        const newRequired = requiredEntriesToRequired(updatedRequired);
        onChange?.((prev) => ({
          ...prev,
          required: newRequired,
          properties: newProperties,
        }));
        return {
          ...prevState,
          propertyEntries: updatedEntries,
          requiredEntries: updatedRequired,
        };
      });
      setOrderKeys((prev) => prev.filter(filterPredicate));
    },
    [onChange]
  );
  const isDuplicate = useCallback(
    (key: string) => propKeys.filter((k) => k === key).length > 1,
    [propKeys]
  );

  const renderParameterEditor = (key: string, idx: number) => {
    const [, property] = propertyEntries[idx];
    const required = requiredEntries[idx].required;

    return (
      <ParameterEditor
        key={idx}
        propertyKey={propertyEntries[idx][0]}
        property={property}
        variant={variant}
        readOnly={readOnly}
        codeEditorExtensions={codeEditorExtensions}
        nestingLevel={nestingLevel}
        required={required}
        idx={idx}
        isDuplicate={isDuplicate(key)}
        handleInputChange={handleInputChange}
        handleRequiredChange={handleRequiredChange}
        handleDelete={handleDelete}
        handleChildChange={handleChildChange}
      />
    );
  };

  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (shouldScroll && schemaScrollRef?.current) {
      schemaScrollRef.current.scrollTo({
        top: schemaScrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
      setShouldScroll(false);
    }
  }, [shouldScroll, schemaScrollRef]);
  const handleAddProperty = useCallback(
    (
      {
        newProperties,
        newRequired,
      }: {
        newProperties: { [property: string]: JsonSchema7 };
        newRequired: string[];
      },
      newKey: string
    ) => {
      setPropertyState((prev) => {
        const newEntry: [string, JsonSchema7] = [newKey, { type: 'string' }];
        const newRequiredEntry: { key: string; required: boolean } = {
          key: newKey,
          required: false,
        };
        const newPropertyEntries = [...prev.propertyEntries, newEntry];
        const newRequiredEntries = [...prev.requiredEntries, newRequiredEntry];
        return {
          propertyEntries: newPropertyEntries,
          requiredEntries: newRequiredEntries,
        };
      });
      setOrderKeys((prev) => [...prev, newKey]);
      onChange?.((prev) => ({
        ...prev,
        properties: {
          ...prev.properties,
          ...newProperties,
        },
        required: [...prev.required, ...newRequired],
      }));
      setShouldScroll(true);
    },
    [setPropertyState, setOrderKeys, onChange]
  );

  return (
    <Stack gap={1}>
      <div className="flex justify-between">
        <FormLabel>
          {pluralize(capitalize(`${VARIANT_PROPERTY_LABEL_MAP[variant]}`), 2)}
        </FormLabel>
        {!readOnly && (
          <AddPropertyToObjectButton
            properties={properties}
            handleAddProperty={handleAddProperty}
            newItemType={
              variant === SchemaEditorVariant.EVALUATOR ? 'boolean' : 'string'
            }
            addLabel={`${VARIANT_PROPERTY_LABEL_MAP[variant]}`}
          />
        )}
      </div>
      <div
        className={cn(
          'flex flex-col gap-4',
          variant !== SchemaEditorVariant.FUNCTIONS && 'bg-transparent p-0',
          variant === SchemaEditorVariant.NESTED && 'pb-2'
        )}
      >
        {orderKeys.map(renderParameterEditor)}

        {(typeof additionalProperties === 'boolean' ||
          additionalProperties === undefined) && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Radio
                size="sm"
                checked={additionalProperties ?? !strict}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  const additionalProperties = e.target.checked;
                  onChange?.((prev) => ({
                    ...prev,
                    additionalProperties,
                    strict: !additionalProperties && prev.strict,
                  }));
                }}
                label="Allow additional properties"
              />
              <Tooltip title="additionalProperties: true (allows properties not defined in the schema)">
                <span>
                  <InfoCircleIcon className="h-3.5 w-3.5 text-disabled" />
                </span>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              <Radio
                size="sm"
                checked={!(additionalProperties ?? !strict) && !strict}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange?.((prev) => ({
                      ...prev,
                      additionalProperties: false,
                      strict: false,
                    }));
                  }
                }}
                label="No additional fields"
              />
              <Tooltip title="additionalProperties: false (disallows properties not defined in the schema)">
                <span>
                  <InfoCircleIcon className="h-3.5 w-3.5 text-disabled" />
                </span>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              <Radio
                size="sm"
                checked={strict ?? false}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  const strictMode = e.target.checked;
                  onChange?.((prev) => ({
                    ...prev,
                    strict: strictMode,
                    additionalProperties:
                      !strictMode && prev.additionalProperties,
                  }));
                }}
                label="Strict"
              />
              <Tooltip title="strict: true (enforces strongest validation, only available for OpenAI models)">
                <span>
                  <InfoCircleIcon className="h-3.5 w-3.5 text-disabled" />
                </span>
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </Stack>
  );
});

const generateUniqueKey = (
  baseKey: string,
  properties: Record<string, any>
): string => {
  let counter = 0;
  let newKey = `${baseKey}${counter}`;
  while (newKey in properties) {
    counter++;
    newKey = `${baseKey}${counter}`;
  }
  return newKey;
};

function AddPropertyToObjectButton({
  properties,
  handleAddProperty,
  newItemType,
  addLabel,
}: {
  properties:
    | {
        [property: string]: JsonSchema7;
      }
    | undefined;
  handleAddProperty: (
    newValue: {
      newProperties: { [property: string]: JsonSchema7 };
      newRequired: string[];
    },
    newKey: string
  ) => void;
  newItemType: 'boolean' | 'string';
  addLabel: string;
}) {
  return (
    <Button
      type="button"
      variant="outlined"
      color="neutral"
      size="sm"
      startDecorator={<PlusIcon className="h-4 w-4" />}
      onClick={() => {
        const newKey = generateUniqueKey('new', properties ?? {});
        const newProperties = {
          [newKey]: {
            type: newItemType,
          },
        };

        const newRequired = [newKey];
        handleAddProperty(
          {
            newProperties,
            newRequired,
          },
          newKey
        );
      }}
      data-testid="add-property-button"
    >
      <span className="capitalize">{addLabel}</span>
    </Button>
  );
}
